import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { ArrowLeft, Languages, Database, FolderSearch, Eye, Loader2 } from 'lucide-react';

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-semibold text-slate-800">{value}</p>
  </div>
);

export default function SuperAdminHistoricalReview() {
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const [coverage, setCoverage] = useState(null);
  const [cache, setCache] = useState(null);
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [translationPreview, setTranslationPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cov, rep, proj] = await Promise.allSettled([
          axiosInstance.get('/superadmin/audit-analysis/translation-coverage'),
          axiosInstance.get('/superadmin/audit-analysis/cache-report'),
          axiosInstance.get('/superadmin/audits/project-name-preview', { params: { limit: 2000 } }),
        ]);
        if (cov.status === 'fulfilled') setCoverage(cov.value.data);
        if (rep.status === 'fulfilled') setCache(rep.value.data);
        if (proj.status === 'fulfilled') setProjects(proj.value.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load the historical review');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const runTranslationPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axiosInstance.post(
        '/superadmin/audit-analysis/translate-backfill',
        { limit: 2, dryRun: true },
      );
      setTranslationPreview(data.results || []);
      toast.success('Preview only — nothing was written');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="historical-review-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Historical data review</h1>
            <p className="text-slate-600 text-sm">
              Read-only. Nothing on this page changes a record.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/superadmin')}
                  data-testid="historical-review-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        {coverage && (
        <Card data-testid="bilingual-coverage-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Languages className="w-4 h-4" /> Bilingual coverage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Audits with analysis" value={coverage?.auditsWithAnalysis ?? 0}
                    testid="stat-with-analysis" />
              <Stat label="English copy" value={coverage?.withEnglishCopy ?? 0}
                    testid="stat-english" />
              <Stat label="Spanish copy" value={coverage?.withSpanishCopy ?? 0}
                    testid="stat-spanish" />
              <Stat label="Needs translation" value={coverage?.needsTranslation ?? 0}
                    testid="stat-needs-translation" />
            </div>
            <p className="text-sm text-slate-600">
              Translation copies the analysis that is already stored into the other language. The
              findings, severities, OSHA references and recommendations are carried over unchanged —
              the model is never asked to re-analyse the job.
            </p>
            <Button size="sm" onClick={runTranslationPreview} disabled={previewing}
                    data-testid="run-translation-preview-btn">
              {previewing
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Translating a sample…</>
                : <><Eye className="w-4 h-4 mr-2" /> Preview a translation (writes nothing)</>}
            </Button>

            {translationPreview?.map((row) => (
              <div key={row.auditId} className="rounded-lg border border-slate-200 p-4 space-y-3"
                   data-testid={`translation-preview-${row.auditId}`}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="outline">{row.auditId?.slice(0, 8)}</Badge>
                  <span className="text-slate-600">{row.location || '—'}</span>
                  <Badge className={row.findingsPreserved ? 'bg-emerald-600' : 'bg-red-600'}>
                    {row.findingsPreserved ? 'Findings preserved' : 'Findings changed — would skip'}
                  </Badge>
                  <Badge variant="secondary">{row.sourceLanguage} → {row.targetLanguage}</Badge>
                </div>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase text-slate-500 mb-1">Stored ({row.sourceLanguage})</p>
                    <p className="text-slate-800">{row.sideBySide?.[`summary_${row.sourceLanguage}`]}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-slate-500 mb-1">Proposed ({row.targetLanguage})</p>
                    <p className="text-slate-800">{row.sideBySide?.[`summary_${row.targetLanguage}`]}</p>
                  </div>
                </div>
                {row.sideBySide?.hazards?.length > 0 && (
                  <div className="text-xs text-slate-600">
                    Severity and OSHA references carried over:{' '}
                    {row.sideBySide.hazards.map((h) => `${h.severity || '—'} / ${h.oshaReference || '—'}`).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
        )}

        <Card data-testid="cache-forensics-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="w-4 h-4" /> AI analysis cache
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Catalog entries" value={cache?.catalog?.total ?? 0} testid="stat-catalog-total" />
              <Stat label="No company id" value={cache?.catalog?.withoutCompanyId ?? 0}
                    testid="stat-catalog-no-company" />
              <Stat label="Source audit deleted" value={cache?.catalog?.sourceAuditMissing ?? 0}
                    testid="stat-catalog-source-missing" />
              <Stat label="Cross-company groups" value={cache?.crossCompanyGroups?.length ?? 0}
                    testid="stat-cross-company" />
            </div>
            <p className="text-sm text-slate-600">{cache?.note}</p>

            {cache?.sharedAnalysisGroups?.length > 0 ? (
              <Table data-testid="shared-analysis-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Shared analysis</TableHead>
                    <TableHead>Audits</TableHead>
                    <TableHead>Companies</TableHead>
                    <TableHead>Cross-company</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cache.sharedAnalysisGroups.slice(0, 20).map((g, i) => (
                    <TableRow key={i} data-testid={`shared-group-row-${i}`}>
                      <TableCell className="max-w-md text-xs">{g.summaryPreview}</TableCell>
                      <TableCell>{g.auditCount}</TableCell>
                      <TableCell>{g.companyIds?.length}</TableCell>
                      <TableCell>
                        {g.crossCompany
                          ? <Badge className="bg-red-600">Yes</Badge>
                          : <Badge variant="secondary">No</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-slate-600" data-testid="no-shared-analysis">
                No two audits currently share an identical AI hazard summary.
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="project-names-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FolderSearch className="w-4 h-4" /> Missing project names
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Missing a name" value={projects?.auditsMissingProjectName ?? 0}
                    testid="stat-missing-project" />
              <Stat label="Evidence-backed" value={projects?.matchedCount ?? 0}
                    testid="stat-matched-project" />
              <Stat label="Left alone" value={projects?.unverifiableCount ?? 0}
                    testid="stat-unverifiable-project" />
            </div>
            {projects?.matched?.length > 0 ? (
              <Table data-testid="project-matches-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Audit</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Proposed name</TableHead>
                    <TableHead>Evidence</TableHead>
                    <TableHead>Confidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.matched.map((m) => (
                    <TableRow key={m.auditId} data-testid={`project-match-row-${m.auditId}`}>
                      <TableCell className="text-xs">{m.auditId.slice(0, 8)}</TableCell>
                      <TableCell className="text-xs">{m.location || '—'}</TableCell>
                      <TableCell className="font-medium">{m.projectName}</TableCell>
                      <TableCell className="text-xs text-slate-600">{m.evidence}</TableCell>
                      <TableCell>
                        <Badge variant={m.confidence === 'high' ? 'default' : 'secondary'}>
                          {m.confidence}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-slate-600" data-testid="no-project-matches">
                No audit has enough evidence to propose a project name.
              </p>
            )}
            <p className="text-xs text-slate-500">
              Nothing is written from this page. Names are only applied after you approve specific
              audit ids.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
